'use client';

/**
 * API namespace `autoWithdraw` — V126-F2-FE-WALLET-AUTO-WITHDRAW-SECTION.
 *
 * Wraps the 3 user-facing endpoints for the daily auto-withdraw cron
 * opt-in + history (mounted under /api/wallet/auto-withdraw-*):
 *
 *   GET   /wallet/auto-withdraw-settings  → getSettings()
 *   PATCH /wallet/auto-withdraw-settings  → updateSettings()
 *   GET   /wallet/auto-withdraw-history   → getHistory()
 *
 * Anti-§6 (CM-19/CM-15): pure thin wrapper over `apiFetch`. NO business
 * logic. The backend controller (`backend/src/controllers/auto-withdraw.
 * controller.ts`) is the source of truth for response shapes — the DTOs
 * here mirror it.
 *
 * Anti-P-041: 0 NEW `as any` in this file.
 * Anti-P-042: 0 NEW `console.*`.
 * Anti-P-013: errors propagate as `ApiClientError` (thrown by `apiFetch`).
 * Anti-P-382: reuses the EXISTING User + Payout models via the backend
 *             endpoints — NO parallel mechanism on the FE.
 * Anti-P-383: file ≤500L target.
 */

import { apiFetch } from '../core/fetch-client';

// ─── Types (mirror backend auto-withdraw.controller.ts response shapes) ─────

/**
 * GET /wallet/auto-withdraw-settings response shape.
 *
 * `autoWithdrawMinAmount` is a Decimal string (mirrors backend
 * `User.wallet.autoWithdrawMinAmount` — Decimal128 as string per
 * SPEC-NEGOCIO §9.2).
 */
export interface AutoWithdrawSettings {
  autoWithdrawEnabled: boolean;
  autoWithdrawMinAmount: string;
}

/**
 * PATCH /wallet/auto-withdraw-settings request body — partial update.
 * At least one field must be present (the backend Zod schema enforces this).
 */
export interface UpdateAutoWithdrawSettingsBody {
  autoWithdrawEnabled?: boolean;
  autoWithdrawMinAmount?: string;
}

/**
 * Single auto-withdraw history row — a Payout document where
 * `autoWithdraw=true`. Mirrors the backend `AutoWithdrawHistoryItem`
 * (controller L132-L148). Field types follow `PayoutDto` from
 * `@profileia/shared/dto/payout` (DecimalString amounts, Network enum,
 * PayoutStatus enum, IsoDateString timestamps).
 */
export interface AutoWithdrawHistoryItem {
  _id: string;
  amount: string;
  fee: string;
  netAmount: string;
  network: 'BSC' | 'Polygon' | 'Arbitrum';
  status:
    | 'awaiting_review'
    | 'pending'
    | 'processing'
    | 'completed'
    | 'failed'
    | 'expired';
  txHash?: string | null;
  failureReason?: string | null;
  createdAt: string;
  completedAt?: string | null;
  autoWithdraw: boolean;
}

/**
 * GET /wallet/auto-withdraw-history response shape — standard paginated
 * envelope (matches `Paginated<T>` from `@profileia/shared/dto/common`).
 */
export interface AutoWithdrawHistoryResponse {
  items: AutoWithdrawHistoryItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Namespace ───────────────────────────────────────────────────────────────

export const autoWithdraw = {
  /**
   * GET /wallet/auto-withdraw-settings — fetch the user's opt-in flag +
   * minimum USD threshold.
   *
   * Auth: requireAuth (mounted on the backend route).
   */
  getSettings(signal?: AbortSignal): Promise<AutoWithdrawSettings> {
    return apiFetch<AutoWithdrawSettings>('/wallet/auto-withdraw-settings', {
      signal,
    });
  },

  /**
   * PATCH /wallet/auto-withdraw-settings — partial update of the user's
   * opt-in flag and/or minimum USD threshold.
   *
   * Auth: requireAuth (mounted on the backend route). NO requireRecent2FA —
   * see backend controller comment for rationale (auto-withdraw flag does
   * NOT change the withdrawal destination address).
   */
  updateSettings(
    body: UpdateAutoWithdrawSettingsBody,
    signal?: AbortSignal,
  ): Promise<AutoWithdrawSettings> {
    return apiFetch<AutoWithdrawSettings>('/wallet/auto-withdraw-settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
      signal,
    });
  },

  /**
   * GET /wallet/auto-withdraw-history?page=1&limit=10 — paginated list of
   * the user's Payout documents where `autoWithdraw=true`, sorted by
   * createdAt desc.
   *
   * Auth: requireAuth (mounted on the backend route).
   */
  getHistory(
    params?: { page?: number; limit?: number },
    signal?: AbortSignal,
  ): Promise<AutoWithdrawHistoryResponse> {
    const query = new URLSearchParams();
    if (params?.page != null && Number.isFinite(params.page) && params.page > 0) {
      query.set('page', String(Math.floor(params.page)));
    }
    if (params?.limit != null && Number.isFinite(params.limit) && params.limit > 0) {
      query.set('limit', String(Math.floor(params.limit)));
    }
    const qs = query.toString();
    const path = qs
      ? `/wallet/auto-withdraw-history?${qs}`
      : '/wallet/auto-withdraw-history';
    return apiFetch<AutoWithdrawHistoryResponse>(path, {
      signal,
    });
  },
};
